import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useMutation } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { GET_APP_INFO_AND_ACCOUNTS } from "../../../api/gql";
import withQuery from "../wrappers/gql/withQuery";

import Sentry from "../../../api/sentry";

import {
  AGGREGATOR_TYPE_PLAID,
  FETCH_ACCOUNTS_RELINK_ERROR_MESSAGE,
  PLAID_EVENT_TYPES,
  REMOVE_ACCOUNTS_ERROR_MESSAGE,
  URI_PATHS,
} from "../../../constants";
import { DEFAULT_LOGO, BASE_64_IMG } from "./consts";

import { ADD_FINICITY_ACCOUNTS, ADD_PLAID_ACCOUNTS, REMOVE_ACCOUNT } from "./gql";
import { FinicityConnect } from "../../linkbank/components";
import LinkBankPlaid from "../../linkbank/LinkBankPlaid";
import Loading from "../../common/Loading";
import { createPlaidEvent, updateToNextStep } from "../../../state/actions";
import ContinueButton from "../common/ContinueButton";
import { Redirect } from "react-router-dom/cjs/react-router-dom.min";

const RelinkBankAccounts = ({ data: applicationInfo, refetchQuery: fetchAccounts }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { getBankAccounts } = applicationInfo;
  const { unlinkedAccountsFound } = getBankAccounts;
  const [refreshAccountsLoading, setRefreshAccountsLoading] = useState(false);
  const [removeAccountById, { loading: removeAccountLoading }] = useMutation(REMOVE_ACCOUNT);
  const [addFinicityAccounts, { loading: finicityAccountUpdatesLoading }] = useMutation(ADD_FINICITY_ACCOUNTS);
  const [addPlaidAccounts, { loading: plaidAccountUpdatesLoading }] = useMutation(ADD_PLAID_ACCOUNTS);
  const [errorNetwork, setErrorNetwork] = useState();

  const refreshAccounts = async () => {
    setRefreshAccountsLoading(true);
    try {
      await fetchAccounts();
    } catch (err) {
      Sentry.captureException(err);
      setErrorNetwork(FETCH_ACCOUNTS_RELINK_ERROR_MESSAGE);
    } finally {
      setRefreshAccountsLoading(false);
    }
  };

  const removeAccount = async (userAccountId) => {
    try {
      await removeAccountById({
        variables: {
          id: userAccountId,
        },
      });
    } catch (err) {
      Sentry.captureException(err);
      setErrorNetwork(REMOVE_ACCOUNTS_ERROR_MESSAGE);
      return;
    }

    await refreshAccounts();
  };

  // Helper Functions Finicity
  const onFormSubmissionFinicity = async () => {
    setErrorNetwork(null);
    try {
      await addFinicityAccounts();
    } catch (err) {
      Sentry.captureException(err);
      setErrorNetwork(FETCH_ACCOUNTS_RELINK_ERROR_MESSAGE);
      return;
    }

    await refreshAccounts();
  };

  const onFormSubmissionPlaid = async (token, metadata) => {
    const { institution } = metadata;
    setErrorNetwork(null);
    try {
      await addPlaidAccounts({
        variables: {
          publicToken: token,
          institution,
        },
      });
    } catch(err){
      Sentry.captureException(err);
      setErrorNetwork(FETCH_ACCOUNTS_RELINK_ERROR_MESSAGE);
      return;
    }

    await refreshAccounts();
  };

  const handlePlaidOnEvent = async (eventType, metadata) => {
    const { error_message: errorMessage } = metadata;

    if((eventType === PLAID_EVENT_TYPES.ERROR || eventType === PLAID_EVENT_TYPES.EXIT) && errorMessage){
      setErrorNetwork(FETCH_ACCOUNTS_RELINK_ERROR_MESSAGE);
      dispatch(createPlaidEvent(metadata));
    }
  };

  const { email } = applicationInfo.getApplicationInfo;
  const { accountsByLogin } = applicationInfo.getBankAccounts;
  const { customer_id: finicityCustomerId } = applicationInfo.finicityConnect;
  const relinkAccounts = (accountsByLogin || []).filter((account) => {
    return account.relink;
  });

  if (relinkAccounts.length === 0) {
    return <Redirect to={URI_PATHS.RESUME_APPLICATION} /> 
  }

  const RenderedRelinkedAccounts = relinkAccounts.map((loginAccount) => {
    const { aggregator, institution, accounts, relink } = loginAccount;
    const { url: linkUrl, linkToken } = relink;
    const { logo, name: institutionName } = institution;
    const fromPlaid = aggregator === AGGREGATOR_TYPE_PLAID;
    const logoData = fromPlaid ? `${BASE_64_IMG}${logo}` : logo;

    const RenderedAccounts = accounts.map((singleAccount) => {
      const { name: accountName, mask, userAccountId } = singleAccount;

      return (
        <div key={accountName + mask} className="flex text-left ml-6 pl-6 rounded mb-1">
          <div className="flex justify-between w-full inline-block">
            <span className="text-gray-500 cursor-default">
              {`${accountName} (**${mask})`}
            </span>
            <button
              className="text-gray-400 hover:text-red-500 text-xs self-center cursor-pointer inline-block mr-4"
              onClick={async () => await removeAccount(userAccountId)}
              type="button"
            >
              Remove
            </button>
          </div>
        </div>
      );
    });

    const relinkButtonClasses = "shadow-xl bg-red-400 hover:bg-red-500 text-sm font-medium text-white px-2 py-1 rounded w-full cursor-pointer";
    const RelinkButton = fromPlaid ?
      <LinkBankPlaid
        linkToken={linkToken}
        overrideButtonClasses={relinkButtonClasses}
        continueStyle
        text="Re-link"
        onEvent={handlePlaidOnEvent}
        onSuccess={onFormSubmissionPlaid}
      /> :
      <FinicityConnect
        linkURL={linkUrl}
        finicityCustomerId={finicityCustomerId}
        email={email}
        buttonText="Re-link"
        buttonClassName={relinkButtonClasses}
        onSuccess={onFormSubmissionFinicity}
        onCancel={onFormSubmissionFinicity}
      />;

    return (
      <>
        <div key={institutionName + logoData} className="flex justify-between p-2">
          <div className="text-left ml-4">
            <img
              className="self-center inline-block h-8"
              src={logo ? logoData : DEFAULT_LOGO} alt="" />
            <span className="ml-2 self-center">{institutionName}</span>
          </div>
          <div>
            {RelinkButton}
          </div>
        </div>
        {RenderedAccounts}
      </>
    );
  });

  const loadingState = removeAccountLoading ||
    finicityAccountUpdatesLoading ||
    plaidAccountUpdatesLoading ||
    refreshAccountsLoading;

  return (
    <>
      {loadingState && <Loading showBackdrop />}
      { errorNetwork && <div className="errorText">{errorNetwork}</div>}
      <div className="font-light text-lg pb-3">
        {unlinkedAccountsFound ? "Accounts" : ""}
      </div>
      <div className="mb-4">
        {RenderedRelinkedAccounts}
      </div>
      <ContinueButton
        loading={loadingState}
        disabled={relinkAccounts.length > 0}
        onClick={async () => {
          dispatch(updateToNextStep(history));
        }}
      />
    </>
  );
};

export default withQuery(GET_APP_INFO_AND_ACCOUNTS)(RelinkBankAccounts);